import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { PlugZap, ArrowRight, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Dashboard reminder: customers who have signed their contract AND paid the
 * deposit but still need a DNO (G98/G99) application raised. Clears once the
 * DNO is raised (via the deep-linked wizard) or an admin dismisses it.
 */
export const DnoRaiseReminderWidget = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState('');
  const navigate = useNavigate();

  const load = () => {
    api.get('/dno/pending-to-raise')
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh so newly-paid deposits surface without a manual reload
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, []);

  const dismiss = async (customerId) => {
    setBusy(customerId);
    try {
      await api.post(`/customers/${customerId}/dno/dismiss-reminder`);
      setItems((prev) => prev.filter((i) => i.customer_id !== customerId));
      toast.success('Reminder dismissed');
    } catch {
      toast.error('Could not dismiss the reminder');
    } finally {
      setBusy('');
    }
  };

  if (!items.length) return null;

  return (
    <Card className="border-indigo-300 bg-indigo-50/60" data-testid="dno-raise-reminder-widget">
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <PlugZap className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">
              {items.length} DNO {items.length === 1 ? 'application' : 'applications'} to raise
            </p>
            <p className="text-sm text-slate-600 mt-0.5">
              These customers have signed and paid their deposit — raise the DNO/G99 application to keep things moving.
            </p>
          </div>
        </div>

        <ul className="space-y-2">
          {items.slice(0, 5).map((it) => (
            <li
              key={it.customer_id}
              className="flex items-center justify-between gap-3 bg-white border border-indigo-100 rounded-lg px-3 py-2"
              data-testid={`dno-raise-item-${it.customer_id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{it.customer_name || 'Customer'}</p>
                <p className="text-xs text-slate-500 truncate">
                  {it.address}{it.postcode ? `, ${it.postcode}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white h-8"
                  onClick={() => navigate(`/dno-applications?customer_id=${it.customer_id}`)}
                  data-testid={`dno-raise-btn-${it.customer_id}`}
                >
                  Raise DNO <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700"
                  onClick={() => dismiss(it.customer_id)}
                  disabled={busy === it.customer_id}
                  title="Dismiss (not applicable)"
                  data-testid={`dno-dismiss-btn-${it.customer_id}`}
                >
                  {busy === it.customer_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
